import { OPTIONAL } from '../../features/consent/consentCatalogue';
import { EssentialTable, ProviderTables } from '../../features/consent/ConsentTables';
import CookieSettingsButton from '../../features/consent/CookieSettingsButton';

// The legal documents, shared by both legal pages: LegalHubLayout (pathbuddy.com)
// and the IVORI Edu legal page (features/marketing/pages/LegalPage.js). Each
// body is plain h2/h3/p/ul markup inside .legal-section blocks, so each page
// styles it in its own design language.
// Products are served at ivoriai.com/edu; the old *.edu.ivoriai.com sub-domains
// are retired and redirect, so the texts name no sub-domain.

function AcceptableUseBody() {
  return (
    <>
      <div className="legal-section">
        <h2>1. Purpose</h2>
        <p>
          This Acceptable Use Policy outlines the rules and guidelines for using Ivori services (including ivoriai.com, IVORI Edu at ivoriai.com/edu, pathbuddy.com, and associated tools).
          It is designed to protect our users, our AI infrastructure, and the broader online community. All users must comply with this policy when using our platform.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. Prohibited Activities</h2>
        <p>You may not use our services to:</p>

        <h3>2.1 Illegal Activities</h3>
        <ul>
          <li>Engage in any activity that violates applicable local, national, or international laws or regulations.</li>
          <li>Facilitate or promote illegal transactions or fraudulent academic impersonation.</li>
          <li>Distribute content that is unlawful or promotes unlawful activities.</li>
        </ul>

        <h3>2.2 Harmful Content & Exploitation</h3>
        <ul>
          <li>Distribute malware, viruses, or other malicious code.</li>
          <li>Engage in phishing, spoofing, or other deceptive practices.</li>
          <li>Send unsolicited bulk messages, automated spam, or artificial prompt injection attacks.</li>
          <li>Host or distribute content that exploits or harms minors in any manner.</li>
        </ul>

        <h3>2.3 Abuse of AI Services & Infrastructure</h3>
        <ul>
          <li>Attempt to gain unauthorized access to any Ivori backend system, network, or user account.</li>
          <li>Interfere with or disrupt the integrity or performance of our services, including denial-of-service (DoS) attacks.</li>
          <li>Use automated scrapers or unauthorized third-party applications to extract question repositories or AI model algorithms.</li>
          <li>Circumvent access controls, rate limits, or subscription tier boundaries.</li>
        </ul>

        <h3>2.4 Intellectual Property Violations</h3>
        <ul>
          <li>Infringe upon the intellectual property rights, trademarks, or copyrights of Ivori or third parties.</li>
          <li>Reproduce, duplicate, decompile, or create derivative works from Ivori prep software or AI modules.</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>3. Enforcement & Reporting</h2>
        <p>
          Ivori reserves the right to suspend, restrict, or terminate access for any user violating these terms.
          Violations may be reported directly to <a href="mailto:support@ivoriai.com">support@ivoriai.com</a>.
        </p>
      </div>
    </>
  );
}

function CookieBody() {
  return (
    <>
      <div className="legal-section">
        <h2>1. Introduction</h2>
        <p>
          Ivori uses cookies and similar storage technologies on our websites at <a href="https://ivoriai.com">https://ivoriai.com</a> (including IVORI Edu at <a href="https://ivoriai.com/edu">https://ivoriai.com/edu</a>) and <a href="https://pathbuddy.com">https://pathbuddy.com</a>. Some are essential to make the site work. Others are optional, and we only use them if you say yes.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. What Are Cookies And Similar Technologies?</h2>
        <p>
          Cookies are small text files that a website stores on your device. Similar technologies do the same job: <strong>localStorage</strong> and <strong>sessionStorage</strong> are places in your browser where a site keeps small pieces of information (sessionStorage is cleared when you close the tab), and pixels and scripts are small pieces of code that can read or write them. This policy covers all of them.
        </p>
      </div>

      <div className="legal-section">
        <h2>3. Essential Storage (Always On)</h2>
        <p>
          The site needs these to work, so they do not need your consent. They are set by us, in your browser, and are not shared with advertisers. Our own code sets no cookies.
        </p>
        <EssentialTable />
      </div>

      <div className="legal-section">
        <h2>4. Optional Cookies (Off Until You Turn Them On)</h2>
        <p>
          Each of the three groups below is off unless you turn it on, and you can turn each on or off separately. Some of these cookies are set by other companies (third parties) when their script runs on our site. Google personalised advertising is always off.
        </p>
        {OPTIONAL.map((cat) => (
          <div key={cat.id}>
            <h3>{cat.title}</h3>
            <p>{cat.purpose}</p>
            <ProviderTables category={cat.id} />
          </div>
        ))}
      </div>

      <div className="legal-section">
        <h2>5. Your Choices</h2>
        <ul>
          <li>The first time you visit, a banner asks you to accept all, reject all, or choose. If you ignore the banner or close the settings without saving, we treat that as no. You can use the whole site either way.</li>
          <li>You can change or withdraw your choice at any time with the "Cookie settings" link in the footer of every page, in the account menu when you are signed in, or with the button below. Withdrawing is as easy as agreeing.</li>
          <li>Your choice lasts 6 months, then we ask again. We also ask again if we change what we use.</li>
          <li>Your choice is kept in your browser (the <code>ivori_cookie_consent</code> item), separately for each site address, so pathbuddy.com and ivoriai.com ask you separately.</li>
          <li>When you withdraw, we stop the tools and delete the Google Analytics, Google Ads and Microsoft Clarity cookies that we are able to delete. Cookies set by other companies on their own domains (for example clarity.ms and doubleclick.net) can only be cleared in your browser settings, and you can also use your browser to block or clear any cookies.</li>
          <li>If you block essential storage in your browser, you will not be able to sign in.</li>
        </ul>
        <p>
          <CookieSettingsButton className="legal-cookie-settings-button">Cookie settings</CookieSettingsButton>
        </p>
      </div>

      <div className="legal-section">
        <h2>6. Children</h2>
        <p>
          Optional cookies are off by default for everyone. If you are under 13, ask a parent or guardian before you turn any of them on.
        </p>
      </div>

      <div className="legal-section">
        <h2>7. Contact And Complaints</h2>
        <p>
          Questions about this policy: <a href="mailto:support@ivoriai.com">support@ivoriai.com</a>. You can also complain to the Information Commissioner's Office at <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
        </p>
      </div>
    </>
  );
}

function DpaBody() {
  return (
    <>
      <div className="legal-section">
        <h2>1. Scope & Purpose</h2>
        <p>
          This Data Processing Agreement ("DPA") applies to the processing of student and user personal data governed by the UK GDPR and EU GDPR across all Ivori educational platforms.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. Processor Obligations</h2>
        <ul>
          <li><strong>Documented Processing:</strong> Ivori processes personal data solely to fulfill user requests, admissions prep services, and platform functionality.</li>
          <li><strong>Confidentiality:</strong> Staff and sub-processors with access to personal data operate under strict confidentiality agreements.</li>
          <li><strong>Security Measures:</strong> Data is encrypted using TLS 1.3 in transit and AES-256 at rest.</li>
          <li><strong>Sub-Processors:</strong> Vetted third-party sub-processors (OpenAI LLC, Anthropic PBC, Stripe Payments Europe Ltd) adhere to equivalent GDPR safeguards.</li>
        </ul>
      </div>
    </>
  );
}

function DisclaimerBody() {
  return (
    <>
      <div className="legal-section">
        <h2>1. Educational Purpose Only</h2>
        <p>
          Ivori materials, practice question banks, and AI feedback modules are designed solely for educational preparation.
          Ivori is an independent platform and is not officially affiliated with Pearson VUE, Cambridge CAAT, UATUK, or university admissions boards.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. No Score Guarantee</h2>
        <p>
          While our prep tools are designed to maximize performance, practice results do not guarantee specific scores or admissions outcomes at target universities.
        </p>
      </div>
    </>
  );
}

function PrivacyBody() {
  return (
    <>
      <div className="legal-section">
        <h2>1. Who We Are & Scope</h2>
        <p>
          This Privacy Policy explains how Ivori ("we", "us", or "our") collects, uses, shares, and protects information when you access or use our websites at <a href="https://ivoriai.com" target="_blank" rel="noopener noreferrer">https://ivoriai.com</a> (including IVORI Edu at <a href="https://ivoriai.com/edu" target="_blank" rel="noopener noreferrer">https://ivoriai.com/edu</a>) and <a href="https://pathbuddy.com" target="_blank" rel="noopener noreferrer">https://pathbuddy.com</a>, and the artificial intelligence ("AI") tutoring, exam simulation, and study tools made available through them (collectively, the "Services").
        </p>
        <p>
          Ivori acts as the Data Controller responsible for your personal data under the UK Data Protection Act 2018, UK GDPR, and EU GDPR.
          Contact our Data Protection Officer at <a href="mailto:support@ivoriai.com">support@ivoriai.com</a>.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. Age Restrictions & Minor Protection</h2>
        <p>
          We do not knowingly collect personal information from children under 13 without verifiable parental or guardian consent. If you are under 13, you must have parental permission to access or use our Services.
          Parents and guardians should monitor their children's online activity. If you believe a child under 13 has provided us with personal information without parental consent, please contact us at <a href="mailto:support@ivoriai.com">support@ivoriai.com</a> so we can take immediate steps to delete the information.
        </p>
      </div>

      <div className="legal-section">
        <h2>3. Information We Collect</h2>

        <h3>3.1 Information You Provide</h3>
        <ul>
          <li><strong>Account & Profile Information:</strong> Full name, email address, password hash or SSO credentials, date of birth, grade level, target subjects (e.g. TMUA, LNAT, CS, Medicine, Law), learning preferences, school name, and profile picture.</li>
          <li><strong>Tutoring & AI Session Information:</strong> Inputs and interactions provided during live tutoring or study sessions—including text prompts, audio/voice recordings (when voice features are activated), whiteboard drawings, uploaded essays/study materials, as well as AI session outputs (responses, hints, step-by-step reasoning, transcripts, and session artifacts).</li>
          <li><strong>Payment & Billing Information:</strong> Billing name, address, and tokenized payment metadata processed by our payment provider (Stripe). We do not store raw credit card numbers on our servers.</li>
          <li><strong>Support & Communications:</strong> Messages, feedback, and customer support inquiries sent to us.</li>
        </ul>

        <h3>3.2 Information Collected Automatically</h3>
        <ul>
          <li><strong>Log and Usage Data:</strong> IP address, device identifiers, browser type, operating system, timestamps, session tokens (JWTs), and crash logs to maintain security, prevent fraud, and diagnose performance issues.</li>
          <li><strong>Cookies & Local Storage:</strong> Essential browser storage keeps you signed in and remembers your settings. Only with your consent, we also use analytics (Google Analytics), session recording (Microsoft Clarity) and advertising (Google Ads conversion measurement; Shown.io, which may also be used to show our adverts to people who have visited this site) cookies. See our Cookie Policy. We do not use your personal data to personalise adverts with Google. Advertising cookies are off unless you turn them on.</li>
        </ul>

        <h3>3.3 Information From Third Parties</h3>
        <ul>
          <li><strong>Single Sign-On (SSO):</strong> Authentication tokens, email, name, and profile avatar from Google OAuth or LinkedIn SSO.</li>
        </ul>

        <h3>3.4 De-Identified & Aggregated Data</h3>
        <ul>
          <li>We may create and use de-identified and aggregated analytics derived from user interactions for AI model quality evaluation, educational research, and product performance improvements. We will not attempt to re-identify de-identified data.</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>4. Third-Party Sub-Processors & Data Sharing</h2>
        <p>We do not sell personal information for monetary value. We share personal information only with vetted third-party service providers acting on our behalf under strict data protection agreements:</p>
        <ul>
          <li><strong>AI Inference & Model Providers:</strong> <strong>OpenAI LLC</strong>, <strong>Anthropic PBC</strong>, and <strong>Google Gemini API</strong> for processing prompts, generating real-time explanations, and evaluating exam responses. (Data submitted via our enterprise API integrations is not used to train public AI models).</li>
          <li><strong>Cloud Infrastructure & Databases:</strong> Secure cloud infrastructure and storage providers (e.g., AWS, DigitalOcean, Supabase).</li>
          <li><strong>Payment Processors:</strong> <strong>Stripe Payments Europe, Ltd.</strong> for PCI-compliant billing and subscription management.</li>
          <li><strong>Analytics & Error Monitoring:</strong> Sentry for error monitoring, to diagnose technical glitches and improve stability. Google Analytics, Microsoft Clarity, Google Ads and Shown.io are used only if you give cookie consent (see our Cookie Policy).</li>
          <li><strong>Email & Transactional Delivery:</strong> Transactional email service providers.</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>5. International Data Transfers</h2>
        <p>
          Transfers of data outside the UK/EEA to our US cloud infrastructure and AI sub-processors are safeguarded under the <strong>UK Extension to the EU-U.S. Data Privacy Framework</strong> and European Commission Standard Contractual Clauses (SCCs).
        </p>
      </div>

      <div className="legal-section">
        <h2>6. Data Retention & Security</h2>
        <p>
          We retain personal information for as long as reasonably necessary to fulfill your study account services, comply with legal obligations, and resolve disputes.
          Session transcripts, study notes, and learning profile data are retained while the account is active and are permanently deleted or de-identified within 30 days of account deletion. We employ TLS 1.3 encryption in transit and AES-256 at rest.
        </p>
      </div>

      <div className="legal-section">
        <h2>7. Your Rights, Choices & Privacy Appeals</h2>
        <p>
          Under UK & EU data protection laws (and applicable US state privacy laws), you have the right to confirm, access, correct, export, or delete your personal data, as well as opt out of non-essential marketing emails.
        </p>
        <p>
          <strong>Privacy Rights Appeal:</strong> If we decline to take action on a privacy request, you may appeal our decision by emailing <a href="mailto:support@ivoriai.com">support@ivoriai.com</a> with the subject line <em>"Privacy Rights Appeal"</em>. You also retain the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
        </p>
      </div>
    </>
  );
}

function RefundBody() {
  return (
    <>
      <div className="legal-section">
        <h2>1. 14-Day Cooling-Off Period</h2>
        <p>
          Under UK Consumer Rights legislation, subscription purchases are eligible for a full refund within 14 days of purchase, provided full digital test downloads or credentials have not been consumed.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. Subscription Cancellation</h2>
        <p>
          You can cancel your subscription at any time via your Profile settings. Access remains active through the end of your billing cycle.
        </p>
      </div>
    </>
  );
}

function SafeguardingBody() {
  return (
    <>
      <div className="legal-section">
        <h2>1. Our Safeguarding Commitment</h2>
        <p>We firmly believe in protecting young adults and students through proactive prevention:</p>
        <ul>
          <li>All individuals are entitled to equal protection rights regardless of age, gender, background, disability, or belief.</li>
          <li>We strictly comply with laws concerning Position of Trust (Sexual Offences Act 2003). Any violation or sharing of private contact details outside the platform is treated with utmost seriousness.</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>2. UK Legal Compliance</h2>
        <p>We comply with UK statutory guidance, including:</p>
        <ul>
          <li>Children Act 1989 & Children Act 2004</li>
          <li>Care Act 2014 & Care and Support Statutory Guidance</li>
          <li>The Prevent Duty (Home Office statutory guidance)</li>
          <li>Working Together to Safeguard Children & Keeping Children Safe in Education (DfE)</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>3. Platform Safety Measures & Recording Storage</h2>
        <ul>
          <li>All interactions within the Ivori platform are tracked and reviewable for safety purposes.</li>
          <li>Automated security mechanisms block attempts to share external contact information.</li>
          <li>Video recordings of mentoring sessions are automatically encrypted and stored online for 30 days for safeguarding purposes.</li>
          <li>Deleted user conversations are retained for 30 days in secure backup archives for safeguarding compliance before permanent purge.</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>4. Community Standards & Prohibited Conduct</h2>
        <ul>
          <li>Prohibits fake profiles, impersonation, or sharing accounts.</li>
          <li>Prohibits hate speech, harassment, intimidating behavior, or romantic/sexual solicitations.</li>
          <li>Prohibits any activity involving minor exploitation or abuse.</li>
        </ul>
      </div>
    </>
  );
}

function TermsBody() {
  return (
    <>
      <div className="legal-section">
        <h2>1. Our Services & Platform Feeds</h2>
        <p>
          With the term "Services", we refer to all features you interact with on the Ivori Platform, including AI bots, study tools, admissions simulators (TMUA, LNAT), networking tools, and motivational/informative posts on your platform feeds.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. Age Requirements & Parental Consent</h2>
        <p>
          Our Services are intended for users aged 13 and older. If we discover an account created by a user under 13 without verifiable parental consent, we will delete the account and associated data immediately.
        </p>
      </div>

      <div className="legal-section">
        <h2>3. User Agreement & Restrictions</h2>
        <p>By using Ivori, you warrant and agree that you will not:</p>
        <ul>
          <li>Reverse engineer, decompile, or extract source code from Ivori software or AI reasoning models.</li>
          <li>Use automated tools or scrapers to harvest content or user information.</li>
          <li>Upload viruses, malicious code, or bypass platform security controls.</li>
          <li>Share personal contact details (phone numbers, personal emails, direct messaging handles) to arrange physical off-platform meetings with other users.</li>
          <li>Discuss subject matter outside designated academic, university admissions, and career preparation domains.</li>
          <li>Engage in offensive or discriminatory behavior bringing Ivori into disrepute.</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>4. Platform Evolution & Rights</h2>
        <p>
          Ivori continuously evolves. We reserve the right to suspend or terminate accounts for non-compliance at our sole discretion.
        </p>
      </div>
    </>
  );
}

export const LEGAL_DOCUMENTS = [
  { id: 'acceptable-use', title: 'Acceptable Use Policy', heading: 'Acceptable Use Policy', path: '/legal/acceptable-use', effective: 'August 2026', updated: 'October 2026', Body: AcceptableUseBody },
  { id: 'cookies', title: 'Cookie Policy', heading: 'Cookie Policy', path: '/cookies', effective: 'August 2026', updated: 'October 2026', Body: CookieBody },
  { id: 'dpa', title: 'Data Processing Agreement', heading: 'Data Processing Agreement (DPA)', path: '/legal/dpa', effective: 'August 2026', Body: DpaBody },
  { id: 'disclaimer', title: 'Disclaimer', heading: 'Disclaimer', path: '/legal/disclaimer', effective: 'August 2026', Body: DisclaimerBody },
  { id: 'privacy', title: 'Privacy Policy', heading: 'Privacy Policy', path: '/privacy', effective: 'August 2026', updated: 'October 2026', Body: PrivacyBody },
  { id: 'refund', title: 'Refund Policy', heading: 'Refund & Cancellation Policy', path: '/legal/refund', effective: 'August 2026', Body: RefundBody },
  { id: 'security', title: 'Security & Safeguarding Policy', heading: 'Security & Safeguarding Policy', path: '/safeguarding', effective: 'August 2026', Body: SafeguardingBody },
  { id: 'terms', title: 'Terms of Service', heading: 'Terms of Service', path: '/terms-of-use', effective: 'August 2026', Body: TermsBody },
];

/** The document a path shows: "/privacy", "/legal/dpa", "/terms" and so on; defaultId otherwise. */
export function legalDocumentIdForPath(pathname, defaultId = 'privacy') {
  const path = pathname || '';
  if (path.includes('/privacy')) return 'privacy';
  if (path.includes('/terms')) return 'terms';
  if (path.includes('/cookies')) return 'cookies';
  if (path.includes('/safeguarding') || path.includes('/security')) return 'security';
  if (path.includes('/acceptable-use')) return 'acceptable-use';
  if (path.includes('/dpa')) return 'dpa';
  if (path.includes('/disclaimer')) return 'disclaimer';
  if (path.includes('/refund')) return 'refund';
  return defaultId;
}

/** The document for an id; the Privacy Policy for an unknown one. */
export function legalDocumentById(id) {
  return LEGAL_DOCUMENTS.find((doc) => doc.id === id)
    || LEGAL_DOCUMENTS.find((doc) => doc.id === 'privacy');
}
